import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { useEffect, Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, RouterProvider, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { UserProfile } from './apis/Profile';
import { GOOGLE_CLIENT_ID } from 'core/constants';
import { getRefreshToken, removeTokens } from 'core/helpers/token';
import { setProfile } from './store/reducers/loginSlice';
import { ErrorBoundary } from 'components/error-boundary';
import WebSocketComponent from 'websocket/WebSocketComponent';

// Non-lazy imports for critical initial pages
import FallbackPage from './pages/Fallback';
import LoginPage from './pages/Login';
import RegistrationPage from './pages/Register';
import ResetPassword from './pages/ResetPassword';

// Lazy-loaded page components for code splitting
const ChatNew = lazy(() => import('./pages/Chat/ChatNew'));
const Home = lazy(() => import('./pages/Home'));
const Onboarding = lazy(() => import('./pages/Onboarding/Onboarding'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings/ProfileSettings'));
const Projects = lazy(() => import('./pages/Projects/Projects'));
const FileUploadModal = lazy(() => import('./pages/ProjectSettings/ProjectSetting'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const PaymentCanceled = lazy(() => import('./pages/Subscriptions/PaymentCanceled'));
const PaymentSuccess = lazy(() => import('./pages/Subscriptions/PaymentSuccess'));
const Subscription = lazy(() => import('./pages/Subscriptions/Subscription'));
const Message = lazy(() => import('pages/Message/Message'));
const SpecialAgentsConfig = lazy(() => import('pages/SpecialAgent/SpecialAgentsConfig'));
const SpecialAgentConfigDetails = lazy(
  () => import('pages/SpecialAgent/SpecialAgentConfigDetails')
);
const RevenueCareAgents = lazy(() => import('pages/SpecialAgent/RevenueCareAgents'));
const SpecialAgentOld = lazy(() => import('pages/SpecialAgent/Project/Task/SpecialAgentOld'));
const SpecialAgentTask = lazy(() => import('pages/SpecialAgent/Project/Task/SpecialAgentTask'));
const CustomPaymentSuccess = lazy(() => import('pages/Subscriptions/CustomPaymentSuccess'));
const CustomPaymentCanceled = lazy(() => import('pages/Subscriptions/CustomPaymentCanceled'));

import 'react-toastify/dist/ReactToastify.css';
import '@llamaindex/pdf-viewer/index.css';
import './App.css';

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0F1419]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const allowedPathsPrefix = [
  '/subscriptions',
  '/custom-subscriptions',
  '/special-agents-hub',
  '/revenue-care-agents'
];

function allowedPaths(path: string) {
  const isAllowed = allowedPathsPrefix.some((prefix) => path.startsWith(prefix));
  return isAllowed;
}

function isPublicPath(path: string) {
  return (
    path === '/login' || path === '/register' || path === '/reset-password' || allowedPaths(path)
  );
}

const WrapperComponent = ({ children }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const refreshToken = getRefreshToken();

  const profile = useSelector((state: any) => state.login.profile);

  const loadUserProfile = async () => {
    const data = await UserProfile.get();
    if (data !== null) {
      dispatch(setProfile(data));
    }
  };

  useEffect(() => {
    if (refreshToken) {
      loadUserProfile();
    } else if (location.pathname === '/') {
      navigate('/special-agents-hub', { replace: true });
    } else if (!isPublicPath(location.pathname)) {
      navigate('/login', { replace: true });
    }
  }, [refreshToken, location.pathname]);

  // Subscription guard disabled - allow all users to access the app directly.
  // useEffect(() => {
  //   if (profile && !profile?.has_any_subscription) {
  //     if (!allowedPaths(location.pathname)) {
  //       navigate('/subscriptions/packages');
  //     }
  //   }
  // }, [profile, location]);

  return (
    <ErrorBoundary>
      <WebSocketComponent profile={profile} />
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

const LogoutHandle = () => {
  const navigate = useNavigate();
  useEffect(() => {
    removeTokens();
    navigate('/login');
  }, []);

  return <h1>Login out...</h1>;
};
export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <WrapperComponent>
        <Home />
      </WrapperComponent>
    )
  },
  {
    path: '/subscriptions/success',
    element: (
      <WrapperComponent>
        <PaymentSuccess />
      </WrapperComponent>
    )
  },
  {
    path: '/subscriptions/canceled',
    element: (
      <WrapperComponent>
        <PaymentCanceled />
      </WrapperComponent>
    )
  },
  {
    path: '/subscriptions/packages',
    element: (
      <Suspense fallback={<PageLoader />}>
        <Subscription />
      </Suspense>
    )
  },
  {
    path: '/custom-subscriptions/success',
    element: (
      <WrapperComponent>
        <CustomPaymentSuccess />
      </WrapperComponent>
    )
  },
  {
    path: '/custom-subscriptions/canceled',
    element: (
      <WrapperComponent>
        <CustomPaymentCanceled />
      </WrapperComponent>
    )
  },
  {
    path: '/login',
    element: (
      <WrapperComponent>
        <LoginPage />
      </WrapperComponent>
    )
  },
  {
    path: '/register',
    element: (
      <WrapperComponent>
        <RegistrationPage />
      </WrapperComponent>
    )
  },
  {
    path: '/logout',
    element: <LogoutHandle />
  },
  {
    path: '/reset-password',
    element: <ResetPassword />
  },
  {
    path: '/agents',
    element: (
      <WrapperComponent>
        <Projects />
      </WrapperComponent>
    )
  },
  {
    path: '/special-agents-hub',
    element: (
      <WrapperComponent>
        <SpecialAgentsConfig />
      </WrapperComponent>
    )
  },
  {
    path: '/revenue-care-agents',
    element: (
      <WrapperComponent>
        <RevenueCareAgents />
      </WrapperComponent>
    )
  },
  {
    path: '/special-agents-hub/:specialAgentConfigId',
    element: (
      <Suspense fallback={<PageLoader />}>
        <SpecialAgentConfigDetails />
      </Suspense>
    )
  },
  {
    path: '/project-settings',
    element: (
      <WrapperComponent>
        <FileUploadModal />
      </WrapperComponent>
    )
  },
  {
    path: '/onboarding',
    element: (
      <WrapperComponent>
        <Onboarding />
      </WrapperComponent>
    )
  },
  {
    path: '/agents/:projectUid/chat',
    element: (
      <WrapperComponent>
        <ChatNew />
      </WrapperComponent>
    )
  },
  {
    path: '/agents/:projectUid/chat/:messageId',
    element: (
      <WrapperComponent>
        <ChatNew />
      </WrapperComponent>
    )
  },
  {
    path: '/agents/:projectUid/settings',
    element: (
      <WrapperComponent>
        <Settings />
      </WrapperComponent>
    )
  },
  {
    path: '/profile-settings',
    element: (
      <WrapperComponent>
        <ProfileSettings />
      </WrapperComponent>
    )
  },
  {
    path: '/agents/:projectUid/chat-widget/:chatWidgetId/customer-chats',
    element: (
      <WrapperComponent>
        <Message />
      </WrapperComponent>
    )
  },
  {
    path: '/special-agents/:projectUid/task',
    element: (
      <WrapperComponent>
        <SpecialAgentTask />
      </WrapperComponent>
    )
  },
  {
    path: '/special-agents/:projectUid/task/:taskId',
    element: (
      <WrapperComponent>
        <SpecialAgentTask />
      </WrapperComponent>
    )
  },
  {
    path: '/special-agents/:projectUid/chat-old',
    element: (
      <WrapperComponent>
        <SpecialAgentOld />
      </WrapperComponent>
    )
  },
  {
    path: '*',
    element: <FallbackPage />
  }
]);

function App() {
  return (
    <div className="App">
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <RouterProvider router={router} />
      </GoogleOAuthProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default App;
